// Basketball Acca product data — thin read hooks over the W1/W2 presentation
// RPCs (get_basketball_hardwood_slip / _spread_fade / _overtime_trap / _powerball
// / _first_half / _q1 / _prop_signals). All HONEST: each RPC returns null or an
// empty board until the dormant spine runs and the calibration gate opens — the
// screens render an honest-empty state, never a fabricated pick.

import { useMemo } from "react";
import { useRpcQuery } from "../../data/useRpcQuery.ts";

/** Generic single-RPC reader (no args). Returns the raw jsonb payload or null. */
function useRpc<T>(fn: string): { loading: boolean; error: string | null; data: T | null } {
  const q = useRpcQuery<T | null>(fn, { refetchInterval: 60_000 });
  const data = useMemo(() => q.data ?? null, [q.data]);
  return useMemo(
    () => ({ loading: q.isLoading, error: q.error ? q.error.message : null, data }),
    [q.isLoading, q.error, data],
  );
}

// ── Hardwood Slip ────────────────────────────────────────────────
export interface SlipLeg {
  game_id: string; market_code: string; selection: string;
  book_odds: number; fair_prob: number; confidence: string | null; cumulative_odds: number;
}
export interface HardwoodSlip {
  slip_date: string | null; rest: boolean; reason?: string | null;
  legs: SlipLeg[]; leg_count: number; combined_odds: number | null;
  in_target_band: boolean; pool_size: number; computed_at: string | null;
}
export const useHardwoodSlip = () => useRpc<HardwoodSlip>("get_basketball_hardwood_slip");

// ── Spread Fade ──────────────────────────────────────────────────
export interface FadeSignal {
  game_id: string; fatigued_team: string; opponent: string;
  signal_type: string; alert_level: string; gauge: string; fatigue_score: number;
  fade_direction: string; book_spread: number; fair_spread: number; spread_gap: number;
  fade_cover_prob: number; expected_edge_pct: number | null; confidence: string | null;
}
export interface SpreadFadeBoard {
  fade_date: string | null; signals: FadeSignal[]; signal_count: number; scanned: number; computed_at: string | null;
}
export const useSpreadFade = () => useRpc<SpreadFadeBoard>("get_basketball_spread_fade");

// ── Overtime Trap ────────────────────────────────────────────────
export interface OvertimeSignal {
  game_id: string; matchup: string; bfpe_ot_prob: number; book_implied_prob: number;
  gap_pct: number; book_odds: number | null; recommended_bet: string; value_pct: number | null; confidence: string;
}
export interface OvertimeTrapBoard {
  trap_date: string | null; signals: OvertimeSignal[]; signal_count: number; scanned: number; computed_at: string | null;
}
export const useOvertimeTrap = () => useRpc<OvertimeTrapBoard>("get_basketball_overtime_trap");

// ── POWERBALL ────────────────────────────────────────────────────
export interface PowerballLeg {
  game_id?: string; market_code?: string; selection?: string;
  book_odds?: number; value_pct?: number; cumulative_odds?: number; confidence?: string | null;
}
export interface PowerballSnapshot {
  week_start: string | null; rest: boolean; legs: PowerballLeg[]; leg_count: number;
  combined_odds: number | null; pool_size: number; computed_at: string | null;
}
export const usePowerball = () => useRpc<PowerballSnapshot>("get_basketball_powerball");

// ── First Half / Q1 (card slot + market-filtered pool) ───────────
export interface MarketCandidate {
  game_id: string; market_code: string; selection: string;
  value_pct?: number; confidence?: string | null; needs_review?: boolean;
}
export interface SlotBoard {
  card_date: string | null; computed_at: string | null;
  pick: MarketCandidate | null; pool: MarketCandidate[]; pool_size: number;
}
export const useFirstHalf = () => useRpc<SlotBoard>("get_basketball_first_half");
export const useQ1 = () => useRpc<SlotBoard>("get_basketball_q1");

// ── Prop Pulse ───────────────────────────────────────────────────
export interface PropSignal {
  game_id: string; player_name: string; team_code: string; market_code: string;
  line: number | null; side: string; fair_prob: number | null; book_odds: number | null;
  edge_pct: number | null; signal: string | null; status: string | null;
}
export interface PropPulseBoard {
  signal_date: string | null; count: number; signals: PropSignal[];
}
export const usePropPulse = () => useRpc<PropPulseBoard>("get_basketball_prop_signals");

// ── The Hardwood Gauntlet ────────────────────────────────────────
export interface GauntletLeg {
  leg_position: number; game_id: string; league: string | null; matchup: string | null;
  market: string; direction: string; leg_odds: number; bfpe_edge_pct: number | null;
  primary_agent: string | null; bfpe_narrative: string | null;
  result: "WON" | "LOST" | "VOID" | "PENDING"; clv_pct: number | null; failure_code: string | null;
}
export interface GauntletRoundMeta { round: number; name: string; thesis?: string; agent?: string; status?: string }
export interface GauntletPerformance {
  total_legs_correct: number | null; rounds_complete: number | null;
  consolation_tier: string | null; jackpot_won: boolean | null; avg_clv_pct: number | null;
}
export interface GauntletSlip {
  week_start: string | null;
  status: "ASSEMBLING" | "READY" | "PUBLISHED" | "NON_PUBLICATION" | "VOID";
  combined_odds: number | null; leg_count: number;
  rounds_meta: GauntletRoundMeta[];
  non_publication_reason: string | null;
  publication_time: string | null; published_at: string | null; computed_at: string | null;
  rounds: Record<string, GauntletLeg[]>; // keyed "1".."8"
  performance: GauntletPerformance | null;
}
export const useGauntlet = () => useRpc<GauntletSlip>("get_basketball_gauntlet");

// ── THE BIG 6 ────────────────────────────────────────────────────
export interface Big6Leg {
  leg_position: number; game_id: string; league: string | null; matchup: string | null;
  market: string; direction: string; odds: number | null; edge_pct: number | null;
  persona_trigger: string | null; bfpe_narrative: string | null; staleness_window: number | null;
  result: "WON" | "LOST" | "VOID" | "PENDING"; clv_pct: number | null;
}
export interface Big6PersonaPool { legs: Big6Leg[]; count: number }
export interface Big6Convergence { game_id: string; personas: string[]; convergence_type: string }
export interface Big6Board {
  date: string | null; total_legs: number;
  personas: Record<string, Big6PersonaPool>; // keyed by persona slug
  convergences: Big6Convergence[];
}
export const useBig6 = () => useRpc<Big6Board>("get_basketball_big6");
